import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

export const Route = createFileRoute("/(public)/privacy")({
  staticData: { title: "Privacy Policy" },
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="relative min-h-screen bg-surface-950 font-sans selection:bg-brand-500/30 selection:text-brand-200">
      <div>
        <div className="mx-auto max-w-4xl px-6 pt-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <h1 className="text-4xl md:text-5xl font-display font-black text-white mb-3">
              Privacy Policy
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
              <p className="text-surface-400 text-sm">Last updated: February 1, 2026</p>
              <span className="text-surface-700 hidden sm:inline">|</span>
              <Link
                to="/terms"
                className="text-sm text-brand-400 hover:text-brand-300 transition-colors inline-flex items-center gap-1"
              >
                Terms of Service <ChevronRight className="size-3" />
              </Link>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <Section id="section-1" title="1. Introduction">
                <p>
                  Rivaled ("we," "our," or "us") is committed to protecting your privacy. This
                  Privacy Policy explains how we collect, use, disclose, and safeguard your
                  information when you use our competitive gaming platform ("the Service").
                </p>
                <p>
                  By using the Service, you agree to the collection and use of information in
                  accordance with this policy.
                </p>
              </Section>

              <Section id="section-2" title="2. Information We Collect">
                <h3 className="text-base font-semibold text-white mt-5">Account Information</h3>
                <p>When you create an account, we collect:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Email address</li>
                  <li>Username and display name</li>
                  <li>Profile picture (if provided)</li>
                  <li>Authentication credentials (securely hashed)</li>
                </ul>

                <h3 className="text-base font-semibold text-white mt-5">Gaming Information</h3>
                <p>To provide our competitive features, we collect:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Game accounts you connect (e.g., Riot ID, Steam ID, Xbox Gamertag)</li>
                  <li>Match history and performance statistics</li>
                  <li>Rankings, ratings (ELO), and competitive progress</li>
                  <li>Club memberships and team affiliations</li>
                </ul>

                <h3 className="text-base font-semibold text-white mt-5">Usage Data</h3>
                <p>We automatically collect information about how you interact with the Service:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Device information (browser type, operating system)</li>
                  <li>IP address and general location</li>
                  <li>Pages visited and features used</li>
                  <li>Time spent on the Service</li>
                  <li>Errors and performance data</li>
                </ul>

                <h3 className="text-base font-semibold text-white mt-5">Communications</h3>
                <p>We collect information from your interactions with other users:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Messages sent through our platform</li>
                  <li>Club chat and team communications</li>
                  <li>Support requests and feedback</li>
                </ul>
              </Section>

              <Section id="section-3" title="3. How We Use Your Information">
                <p>We use the collected information to:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    <strong>Provide the Service</strong> — Facilitate matchmaking, rankings, and
                    competitive features
                  </li>
                  <li>
                    <strong>Improve the Service</strong> — Analyze usage patterns and optimize
                    performance
                  </li>
                  <li>
                    <strong>Communicate</strong> — Send notifications about matches, rankings, and
                    platform updates
                  </li>
                  <li>
                    <strong>Ensure Safety</strong> — Detect and prevent fraud, cheating, and abusive
                    behavior
                  </li>
                  <li>
                    <strong>Process Payments</strong> — Handle subscriptions and billing (processed
                    by Polar)
                  </li>
                  <li>
                    <strong>Comply with Legal Obligations</strong> — Respond to legal requests and
                    enforce our Terms of Service
                  </li>
                </ul>
              </Section>

              <Section id="section-4" title="4. Information Sharing and Disclosure">
                <p>We do not sell your personal information. We may share your information with:</p>

                <h3 className="text-base font-semibold text-white mt-5">Other Users</h3>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    Your public profile, statistics, and match history are visible to other users
                  </li>
                  <li>Club members can see your activity within the club</li>
                </ul>

                <h3 className="text-base font-semibold text-white mt-5">Service Providers</h3>
                <p>We work with third-party companies that help us operate the Service:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    <strong>Polar</strong> — Payment processing and billing
                  </li>
                  <li>
                    <strong>Cloud Infrastructure</strong> — Hosting and database services
                  </li>
                  <li>
                    <strong>Analytics</strong> — Service monitoring and improvement
                  </li>
                </ul>

                <h3 className="text-base font-semibold text-white mt-5">Legal Requirements</h3>
                <p>We may disclose your information if required by law, court order, or to:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Protect our rights and property</li>
                  <li>Prevent fraud or security threats</li>
                  <li>Protect the safety of users or the public</li>
                </ul>
              </Section>

              <Section id="section-5" title="5. Data Retention">
                <p>We retain your information based on the following:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Account data is kept for as long as your account is active</li>
                  <li>
                    You may request deletion at any time through your account settings or by
                    contacting{" "}
                    <a
                      href="mailto:support@rivaled.gg"
                      className="text-brand-400 hover:text-brand-300 transition-colors"
                    >
                      support@rivaled.gg
                    </a>
                  </li>
                  <li>
                    Some information may be retained longer as required by law or for legitimate
                    business purposes (e.g., preventing abuse, resolving disputes)
                  </li>
                </ul>
              </Section>

              <Section id="section-6" title="6. Data Security">
                <p>We take the following measures to protect your information:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Industry-standard encryption for sensitive data</li>
                  <li>Regular review of security practices and infrastructure</li>
                  <li>
                    Protection against unauthorized access, alteration, disclosure, and destruction
                  </li>
                </ul>
                <p>
                  However, no method of transmission over the Internet or electronic storage is 100%
                  secure.
                </p>
              </Section>

              <Section id="section-7" title="7. Your Rights and Choices">
                <p>You have the right to:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    <strong>Access</strong> — Request a copy of the personal information we hold
                    about you
                  </li>
                  <li>
                    <strong>Correction</strong> — Update or correct inaccurate information through
                    your account settings
                  </li>
                  <li>
                    <strong>Deletion</strong> — Request deletion of your account and personal data
                  </li>
                  <li>
                    <strong>Opt-Out</strong> — Unsubscribe from marketing communications
                  </li>
                  <li>
                    <strong>Data Portability</strong> — Request your data in a portable format
                  </li>
                </ul>
                <p>
                  To exercise these rights, contact us at{" "}
                  <a
                    href="mailto:support@rivaled.gg"
                    className="text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    support@rivaled.gg
                  </a>{" "}
                  or through your account settings.
                </p>
              </Section>

              <Section id="section-8" title="8. Children's Privacy">
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Our Service is not directed to children under 13</li>
                  <li>We do not knowingly collect personal information from children under 13</li>
                  <li>
                    Users between 13 and 18 should have parental or guardian consent before using
                    the Service
                  </li>
                </ul>
                <p>
                  If you are a parent or guardian and believe your child has provided us with
                  personal information, please contact us at{" "}
                  <a
                    href="mailto:support@rivaled.gg"
                    className="text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    support@rivaled.gg
                  </a>
                  , and we will delete such information.
                </p>
              </Section>

              <Section id="section-9" title="9. International Data Transfers">
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    Your information may be transferred to and maintained on servers located outside
                    your jurisdiction where data protection laws may differ
                  </li>
                  <li>
                    By using the Service, you consent to the transfer of your information to the
                    United States and other countries where we operate
                  </li>
                </ul>
              </Section>

              <Section id="section-10" title="10. Cookies and Tracking Technologies">
                <p>We use cookies and similar technologies to:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Maintain your session and remember your preferences</li>
                  <li>Analyze Service usage and performance</li>
                  <li>Provide personalized features</li>
                </ul>
                <p>
                  You can control cookie settings through your browser, but some features may not
                  function properly if cookies are disabled.
                </p>
              </Section>

              <Section id="section-11" title="11. Third-Party Links">
                <p>
                  The Service may contain links to third-party websites or services (e.g., Discord,
                  game platforms). We are not responsible for the privacy practices of these third
                  parties. We encourage you to review their privacy policies.
                </p>
              </Section>

              <Section id="section-12" title="12. Changes to This Privacy Policy">
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>We may update this Privacy Policy from time to time</li>
                  <li>
                    We will notify you of material changes by posting the updated policy and
                    updating the "Last updated" date
                  </li>
                  <li>
                    Your continued use of the Service after changes are posted constitutes
                    acceptance of the updated policy
                  </li>
                </ul>
              </Section>

              <Section id="section-13" title="13. Contact Us">
                <p>
                  If you have questions about this Privacy Policy or our data practices, please
                  contact us:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    Email:{" "}
                    <a
                      href="mailto:support@rivaled.gg"
                      className="text-brand-400 hover:text-brand-300 transition-colors"
                    >
                      support@rivaled.gg
                    </a>
                  </li>
                  <li>
                    Discord:{" "}
                    <a
                      href="https://discord.gg/gxwdZKRZ"
                      className="text-brand-400 hover:text-brand-300 transition-colors"
                    >
                      discord.gg/gxwdZKRZ
                    </a>
                  </li>
                </ul>
              </Section>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-28 rounded-xl bg-surface-800/30 border border-surface-700/30 p-4"
    >
      <h2 className="text-xl font-display font-bold text-white mb-3">{title}</h2>
      <div className="text-surface-300 space-y-3 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}
